import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Container } from "reactstrap";
import { FlexboxGrid, ButtonGroup, Button, IconButton, Message } from "rsuite";
import StopSellIcon from "../../assets/img/stop-sell-icon.svg?react";
import UploadExcelIcon from "../../assets/img/upload-excel-icon.svg?react";
import cx from "classnames";
import CalendarView from "../../components/calendar-view/CalendarView";
import RatePlan from "../../components/ratePlan/RatePlan";
import ManageAri from "../../components/manageari-filters/ManageAri";
import TabularView from "../../components/tabular-view/TabularView";
import { usePropertyStore } from "../../contexts/PropertyStoreContext";
import ariSelectRateplan from "../../assets/img/ari-select-rateplan.png";
import { OFFLINE_CRS_ID } from "../../constants";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedRateplan,
  selectHasNegotiatedRateplans,
} from "../../slices/rateplanSlice";
import CalendarIcon from "../../assets/img/calendar-icon.svg?react";
import TableIcon from "../../assets/img/table-icon.svg?react";
import { AriProvider } from "../../contexts/AriContext";
import { fetchRoomtypes } from "../../slices/roomtypeSlice";
import { selectManageARIDateRange } from "../../slices/ariDateRangeSlice";
import EditableRatePlanFilter from "../../components/editable-rateplan-filter/EditableRatePlanFilter";
import UploadExcelModal from "../../components/upload-excel-modal/UploadExcelModal";
import StopSellPanel from "../../components/stop-sell/StopSellPanel";
import { Can, P } from "../../rbac";
import "./ari.scss";

export const SHE_VIEW_MODE = {
  QUICK_VIEW: "QUICK_VIEW",
  MANAGE_ARI: "MANAGE_ARI",
};

const SHOW_MAINTENANCE_BANNER = false; // Set to true to show the maintenance banner

const getDefaultValueMode = () =>
  localStorage.getItem("viewMode") ?? SHE_VIEW_MODE.QUICK_VIEW;

const getLandingViewMode = (modeInParams) => {
  if (modeInParams) {
    return modeInParams;
  }
  return getDefaultValueMode();
};

const Ari = () => {
  const { selectedProperty } = usePropertyStore();
  let navigate = useNavigate();
  let [params] = useSearchParams();
  const isOffline = selectedProperty?.crsId === OFFLINE_CRS_ID;
  const [activeView, setActiveView] = useState(
    getLandingViewMode(params.get("viewMode"))
  );
  const selectedRateplan = useSelector(selectSelectedRateplan);
  const manageAriDateRange = useSelector(selectManageARIDateRange);
  const hasNegotiated = useSelector(selectHasNegotiatedRateplans);
  const dispatch = useDispatch();
  const [showUploadExcel, setShowUploadExcel] = useState(false);
  const [showStopSell, setShowStopSell] = useState(false);

  const showUploadButton = isOffline || (!isOffline && hasNegotiated);
  const showStopSellButton = isOffline || hasNegotiated;

  // This will handle left navigation click on manageARI as current view
  useEffect(() => {
    if (selectedProperty?.propertyId) {
      const viewMode = getLandingViewMode(params.get("viewMode"));
      setActiveView(viewMode);
      navigate(`/ari?viewMode=${viewMode}`, {
        replace: true,
      });
    }
  }, [isOffline, selectedProperty?.propertyId, params, navigate]);

  useEffect(() => {
    if (selectedProperty?.propertyId) {
      dispatch(fetchRoomtypes(selectedProperty.propertyId));
    }
  }, [selectedProperty, dispatch]);

  const handleViewChange = (view) => {
    if (view !== activeView) {
      setActiveView(view);
      navigate(`/ari?viewMode=${view}`, { replace: true });
      localStorage.setItem("viewMode", view);
    }
  };

  const renderPageView = useCallback(() => {
    if (
      !selectedRateplan ||
      (activeView === SHE_VIEW_MODE.MANAGE_ARI && !manageAriDateRange)
    ) {
      return (
        <img
          src={ariSelectRateplan}
          alt="select rateplan"
          className="mx-auto d-block"
        />
      );
    }
    if (activeView === SHE_VIEW_MODE.QUICK_VIEW) {
      return (
        <CalendarView
          propertyId={selectedProperty?.propertyId}
          rateplan={selectedRateplan}
          isOffline={isOffline}
        />
      );
    } else {
      return <TabularView rateplan={selectedRateplan} />;
    }
  }, [
    selectedRateplan,
    activeView,
    manageAriDateRange,
    selectedProperty?.propertyId,
    isOffline,
  ]);

  // Don't render content until property is loaded to prevent flickering
  if (!selectedProperty?.propertyId) {
    return null;
  }

  return (
    <Container
      id="she-home"
      data-heap-id="she-home"
      fluid
      className="she-container"
    >
      <FlexboxGrid
        justify="space-between"
        align="middle"
        className="ari-header-row"
      >
        <FlexboxGrid.Item>
          <p className="page-header" data-testid="ari-header">
            ARI Management
          </p>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item>
          <FlexboxGrid align="middle" className="ari-header-actions">
            {showStopSellButton && (
              <Can permission={P.Ari.StopSell}>
                <IconButton
                  appearance="ghost"
                  icon={<StopSellIcon />}
                  className="ari-action-btn"
                  onClick={() => setShowStopSell(true)}
                >
                  Stop-Sell
                </IconButton>
              </Can>
            )}
            {showUploadButton && (
              <Can permission={P.Ari.Template.Upload}>
                <IconButton
                  appearance="ghost"
                  icon={<UploadExcelIcon />}
                  className="ari-action-btn"
                  onClick={() => setShowUploadExcel(true)}
                >
                  Upload Excel
                </IconButton>
              </Can>
            )}
          </FlexboxGrid>
        </FlexboxGrid.Item>
      </FlexboxGrid>

      {SHOW_MAINTENANCE_BANNER && (
        <div className="mb-5">
          <Message type="warning" bordered>
            <p className="mb-0">
              Due to a critical issue with our ARI (Availability, Rates, and
              Inventory) synchronization systems, ARI data may appear outdated
              or incomplete, and any changes made may not be saved or reflected.
            </p>
            <p className="my-0">
              Our engineering team is working urgently to resolve the issue and
              we expect service to resume within the next 4–6 hours. We
              apologize for any inconvenience and appreciate your understanding.
            </p>
          </Message>
        </div>
      )}

      <FlexboxGrid
        justify="space-between"
        align="middle"
        className={cx({
          "quick-view-active": activeView === SHE_VIEW_MODE.QUICK_VIEW,
          "manage-ari-active": activeView !== SHE_VIEW_MODE.QUICK_VIEW,
        })}
      >
        <FlexboxGrid.Item>
          <FlexboxGrid align="middle" className="ari-filters">
            <EditableRatePlanFilter />
            {activeView === SHE_VIEW_MODE.QUICK_VIEW ? (
              <RatePlan data-testid="calendar-rate-plan" />
            ) : (
              <ManageAri />
            )}
          </FlexboxGrid>
        </FlexboxGrid.Item>

        <FlexboxGrid.Item>
          <FlexboxGrid align="middle">
            <ButtonGroup className="toggle-container">
              <Button
                className={cx({
                  "active-view": activeView === SHE_VIEW_MODE.QUICK_VIEW,
                })}
                onClick={() => handleViewChange(SHE_VIEW_MODE.QUICK_VIEW)}
                startIcon={<CalendarIcon />}
              >
                Calendar
              </Button>
              <Button
                className={cx({
                  "active-view": activeView === SHE_VIEW_MODE.MANAGE_ARI,
                })}
                onClick={() => handleViewChange(SHE_VIEW_MODE.MANAGE_ARI)}
                startIcon={<TableIcon />}
              >
                Table
              </Button>
            </ButtonGroup>
          </FlexboxGrid>
        </FlexboxGrid.Item>
      </FlexboxGrid>
      <AriProvider>{renderPageView()}</AriProvider>

      <UploadExcelModal
        open={showUploadExcel}
        onClose={() => setShowUploadExcel(false)}
      />
      <StopSellPanel
        open={showStopSell}
        onClose={() => setShowStopSell(false)}
        isOffline={isOffline}
      />
    </Container>
  );
};

export default Ari;
